import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row } from 'reactstrap'
import {
  chooseConfigurationOption,
  resolveConfigurationSelection,
  type BundleSelections,
  type ConfigurationSet
} from '../../configurationSelection'
import ConfigurableProduct from './ConfigurableProduct'

type BundleProduct = {
  product_id: number
  bundle_group_product_id: number
  product_sku: string
  nested_data: ConfigurationSet[]
  variants: ConfigurationSet[]
  allow_backorder_inventory?: boolean
}

type StoreState = {
  productPage: {
    configurableSelection: BundleSelections
    availableSkus: Record<number, string[]>
  }
  storeViews: { current: { show_price: boolean } }
}

const Configurable = ({ product }: { product: BundleProduct }) => {
  const dispatch = useDispatch()
  const configSelection = useSelector((state: StoreState) => state.productPage.configurableSelection)
  const currentLocation = useSelector((state: StoreState) => state.storeViews.current)
  const availableSkus = useSelector((state: StoreState) => state.productPage.availableSkus[product.product_id])
  const sets = product.nested_data.filter(set => !!set.variant_id)
  const selections = configSelection[product.bundle_group_product_id]?.selected_options ?? {}
  const resolved = resolveConfigurationSelection(sets, selections)

  useEffect(() => {
    if (resolved.defaults.length === 0) return
    dispatch({
      type: 'DEFAULT_CONFIG_SELECTION',
      payload: {
        product_id: product.product_id,
        bundle_group_product_id: product.bundle_group_product_id,
        options: Object.fromEntries(resolved.defaults.map(({ variant_id, option }) => [variant_id, option]))
      }
    })
  }, [dispatch, product.product_id, product.bundle_group_product_id, configSelection, resolved.defaults])

  const selectOption = (variantId: number, optionId: number) => {
    const next = chooseConfigurationOption(sets, selections, variantId, optionId)
    const option = next.selections[variantId]
    if (!option || option === selections[variantId]) return
    dispatch({
      type: 'UPDATE_CONFIG_SELECTION',
      payload: {
        product_id: product.product_id,
        bundle_group_product_id: product.bundle_group_product_id,
        variant_id: variantId,
        option
      }
    })
  }

  return (
    <Row>
      {sets.map(variant => (
        <ConfigurableProduct
          key={variant.variant_id}
          variant={variant}
          product={product}
          sets={sets}
          selections={resolved.selections}
          selectOption={selectOption}
          currentLocation={currentLocation}
          availableSkus={availableSkus ?? []}
        />
      ))}
    </Row>
  )
}

export default Configurable
